/**
 * AppBar clinic scope menu: switch working clinic, open that clinic's
 * settings without changing scope, open the clinic list, and create a
 * clinic when permitted. Clinic admin lives here — not the HR rail.
 */
import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';
import { clinicDisplayName } from '@/shared/clinic';
import { usePermission } from '@/shared/hooks/usePermission';
import { Button, IconButton, Tooltip } from '@/shared/ui';
import { elevation, radius } from '@/shared/theme/tokens';

interface ClinicOption {
  _id: string;
  name: string;
  shortName?: string | null;
}

interface ClinicSwitcherProps {
  clinics: ClinicOption[];
  selected: string;
  canViewAllClinics: boolean;
  onClinicChange: (clinicId: string) => void;
  /** Cap trigger at parent width (AppBar sidebar column); still shrink-wraps label. */
  fullWidth?: boolean;
}

const clinicInitials = (label: string): string => {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ''}${parts[1][0] ?? ''}`.toUpperCase();
};

const ClinicAvatar: React.FC<{ label: string; size?: number }> = ({
  label,
  size = 24,
}) => (
  <Avatar
    sx={{
      width: size,
      height: size,
      fontSize: size <= 24 ? '0.65rem' : '0.75rem',
      fontWeight: 600,
      bgcolor: 'primary.main',
    }}
  >
    {clinicInitials(label)}
  </Avatar>
);

const ClinicSwitcher: React.FC<ClinicSwitcherProps> = ({
  clinics,
  selected,
  canViewAllClinics,
  onClinicChange,
  fullWidth = false,
}) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canReadClinics = hasPermission('clinics:read');
  const canCreateClinic = hasPermission('clinics:manage');
  const menuId = useId();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const effectiveId = clinics.length === 1 ? clinics[0]._id : selected;
  const currentClinic =
    effectiveId === 'all'
      ? null
      : clinics.find((c) => c._id === effectiveId) ?? clinics[0] ?? null;
  const currentLabel =
    effectiveId === 'all'
      ? t('allClinics')
      : clinicDisplayName(currentClinic) || t('clinic');

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (clinicId: string) => {
    handleClose();
    if (clinicId !== effectiveId) {
      onClinicChange(clinicId);
    }
  };

  const handleOpenSettings = (clinicId: string) => {
    handleClose();
    navigate(`/clinics/${clinicId}`);
  };

  const handleManageClinics = () => {
    handleClose();
    navigate('/clinics');
  };

  const handleCreateClinic = () => {
    handleClose();
    navigate('/clinics', { state: { openAddClinic: true } });
  };

  return (
    <>
      <Button
        intent="ghost"
        color="inherit"
        onClick={handleOpen}
        aria-label={t('clinic')}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        endIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              color: 'text.secondary',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: (theme) =>
                theme.transitions.create('transform', {
                  duration: theme.transitions.duration.shorter,
                }),
            }}
          />
        }
        sx={{
          textTransform: 'none',
          color: 'text.primary',
          fontWeight: 500,
          // Shrink-wrap label; grow up to sidebar column, then truncate.
          width: 'auto',
          maxWidth: fullWidth ? '100%' : 240,
          justifyContent: 'flex-start',
          px: 1,
          py: 0.5,
          borderRadius: radius.sm,
          gap: 0.5,
          minWidth: 0,
          overflow: 'hidden',
          '& .MuiButton-endIcon': { ml: 0.25 },
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography
          component="span"
          variant="body2"
          noWrap
          sx={{ fontWeight: 500, minWidth: 0, textAlign: 'left' }}
        >
          {currentLabel}
        </Typography>
      </Button>

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{
          paper: {
            elevation: elevation.menu,
            sx: {
              mt: 0.75,
              minWidth: 260,
              borderRadius: radius.md,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            },
          },
          list: {
            dense: true,
            sx: { py: 0.5 },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            color: 'text.secondary',
          }}
        >
          <SwapHorizIcon sx={{ fontSize: 18 }} />
          <Typography variant="body2" color="text.secondary">
            {t('switchClinic')}
          </Typography>
        </Box>

        <Divider />

        {canViewAllClinics && clinics.length > 1 && (
          <MenuItem
            selected={effectiveId === 'all'}
            onClick={() => handleSelect('all')}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: 'action.selected',
                  color: 'text.secondary',
                }}
              >
                <BusinessIcon sx={{ fontSize: 14 }} />
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={t('allClinics')}
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        )}

        {clinics.map((clinic) => {
          const label = clinicDisplayName(clinic);
          return (
            <MenuItem
              key={clinic._id}
              selected={effectiveId === clinic._id}
              onClick={() => handleSelect(clinic._id)}
              sx={{ pr: canReadClinics ? 0.5 : undefined }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <ClinicAvatar label={label} />
              </ListItemIcon>
              <ListItemText
                primary={label}
                slotProps={{ primary: { variant: 'body2', noWrap: true } }}
                sx={{ minWidth: 0, mr: canReadClinics ? 0.5 : undefined }}
              />
              {canReadClinics && (
                <Tooltip title={t('clinicSettings')}>
                  <IconButton
                    size="small"
                    aria-label={t('clinicSettingsAria', { name: label })}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleOpenSettings(clinic._id);
                    }}
                    onMouseDown={(event) => event.stopPropagation()}
                    sx={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </MenuItem>
          );
        })}

        {canReadClinics || canCreateClinic ? <Divider /> : null}
        {canReadClinics ? (
          <MenuItem onClick={handleManageClinics}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <BusinessIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('manageClinics')}
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        ) : null}
        {canCreateClinic ? (
          <MenuItem onClick={handleCreateClinic}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <AddIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('createNewClinic')}
              slotProps={{ primary: { variant: 'body2' } }}
            />
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
};

export default ClinicSwitcher;
